import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '../hooks/useAuth'
import { useC101UserGet } from '../hooks/useC101UserGet'
import { useC102UserUpdate } from '../hooks/useC102UserUpdate'
import { emailSchema } from '../schema/emailSchema'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'

// メールアドレス更新画面
export const Email = () => {
  // hooks
  const auth = useAuth()
  const c101 = useC101UserGet()
  const c102 = useC102UserUpdate()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  const [currentEmail, setCurrentEmail] = useState('')
  const [email, setEmail] = useState('')

  const [isDisabled, setIsDisabled] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      // ユーザー取得
      if (auth.loginId) {
        const result = await c101.execUserGet(auth.loginId)
        if (result.success) {
          if (result.info) {
            // console.log("execUserGet");
            setCurrentEmail(result.info!.email)
          }
        } else {
          setErrorDialog(result.message!)
          setOpenDialog(true)
        }
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validate = async (value: string) => {
    try {
      if (value === currentEmail) {
        // 現メールアドレスと同じ場合は、ボタン非活性
        setErrorMessage('')
        setIsDisabled(true)
      } else {
        await emailSchema.validate(value)
        setErrorMessage('')
        setIsDisabled(false)
      }
    } catch (err: any) {
      setErrorMessage(err.message)
      setIsDisabled(true)
    }
  }

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    validate(event.target.value)
  }

  const onClickEmailUpdate = async () => {
    if (auth.loginId !== '') {
      // ユーザー更新（メールアドレス）
      const result = await c102.execUserUpdate(auth.loginId, email)
      if (result.success) {
        // console.log("execUserUpdate succeed");
        setEmail('')
        setIsDisabled(true)
        // ユーザー取得
        const resultGet = await c101.execUserGet(auth.loginId)
        if (resultGet.success) {
          // console.log("execUserGet succeed");
          setCurrentEmail(resultGet.info!.email)
        } else {
          setErrorDialog(resultGet.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }
  }
  const onDialogClosekHandler = async () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let emailChangeButtonJsx: JSX.Element
  switch (baseLanguage) {
    case 'it':
      emailChangeButtonJsx = (
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{
            width: '220px',
            height: '49px',
            borderRadius: '10px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 21px/29px Noto Sans CJK JP',
            letterSpacing: '0px',
            color: '#FFFFFF',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          disabled={isDisabled}
          style={{
            backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
          }}
          onClick={onClickEmailUpdate}
        >
          {translation['button_change']}
        </Button>
      )
      break
    default:
      emailChangeButtonJsx = (
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{
            width: '181px',
            height: '49px',
            borderRadius: '10px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 21px/29px Noto Sans CJK JP',
            letterSpacing: '0px',
            color: '#FFFFFF',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          disabled={isDisabled}
          style={{
            backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
          }}
          onClick={onClickEmailUpdate}
        >
          {translation['button_change']}
        </Button>
      )
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog open={openDialog} text={errorDialog} onClick={onDialogClosekHandler} />
      )}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
        }}
      >
        {(c102.loading === loadStatus.LOADING || auth.loading === loadStatus.LOADING) && (
          <CircularProgress />
        )}
      </Box>
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'auto',
            width: '100%',
            marginTop: '80px',
          }}
        >
          <Paper
            elevation={5}
            sx={{
              height: '762px',
              width: '787px',
              boxShadow: '0px 2px 2px #00000026',
              borderRadius: '10px',
            }}
          >
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
              <Stack direction="column">
                <Typography
                  sx={{
                    mt: '140px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  {translation['label_current_email']}
                </Typography>
                <TextField
                  variant="outlined"
                  value={currentEmail}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      background: '#F4F4F4',
                    },
                  }}
                  sx={{
                    mt: '5px',
                    width: '402px',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#282828',
                      },
                    },
                  }}
                />
                <Typography
                  sx={{
                    mt: '52px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  {translation['label_new_email_address']}
                </Typography>
                <TextField
                  justify-content="center" /* 水平方向の中央揃え */
                  variant="outlined"
                  value={email}
                  error={!!errorMessage}
                  helperText={errorMessage ? translation[errorMessage] : ' '}
                  onChange={handleEmailChange}
                  InputProps={{
                    sx: {
                      background: '#F4F4F4',
                    },
                  }}
                  sx={{
                    mt: '5px',
                    width: '402px',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: errorMessage ? 'red' : '#282828',
                      },
                    },
                  }}
                />
              </Stack>
              <Box mt={4}>{emailChangeButtonJsx}</Box>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
